import {
  getRefCodeAndTracking,
  PAGELIST_ID,
} from '@jobseeker/common-core/constants';
import {handleTracking} from '@jobseeker/common-core/utils';
import {
  OutlineCicleCheck,
  OutlineCircleCross,
} from '@jobseeker/lotus/assets/icons';
import Dialog from '@jobseeker/lotus/components/Dialog';
import BottomSheet from '@jobseeker/lotus/elements/BottomSheet';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import type {} from '@jobseeker/lotus/mui.types';
import {Box, Grid, List, ListItem, Typography} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useRouter} from 'next/router';
import {useEffect} from 'react';
import PayuForm from '../Membership/PayuForm';
import type {ProMembershipPromptType} from './types';
import { PaidProducts } from '../../constant';

const ProMembershipPlanPopUp = ({
  open,
  handleClose,
  planPopUp,
  title,
  subtitle,
}: ProMembershipPromptType) => {
  const theme = useWrappedTheme();
  const isLarge = useIsLargeDevice();
  const isAboveMd = useIsLargeDevice('md');
  const WrapperComponent = isLarge ? Dialog : BottomSheet;

  useEffect(() => {
    handleTracking('jsViewProPopUp', {
      event_category: 'UpgradeToPro',
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    });
  }, []);

  // const ProButton = () => (
  //   <>
  //     <PayuForm
  //       key={'pro_membership_plan_button'}
  //       title="Buy Now @ &#8377;2000/6 months"
  //       refCode="30"
  //       pageId={PAGELIST_ID['jd']}
  //       tracking={{action: 'jsSCBanner_PayClick', category: 'UpgradeToPro'}}
  //     />
  //     <Typography
  //       fontSize={theme.typography.pxToRem(8)}
  //       fontStyle={'italic'}
  //       fontWeight={500}
  //       lineHeight={'10px'}
  //       letterSpacing={'0.32px'}
  //       mt={0.75}
  //       mb={{xxs: 0, md: 1.75}}
  //       display={{xxs: 'none', md: 'block'}}
  //     >
  //       {`(Less than Rs 15/day)`}
  //     </Typography>
  //   </>
  // );

  const ProButton = () => {
    const router = useRouter();
    const queryParams = router.query;
    const isRetry = router.query.retry === 'true';
    return (
      <>
        <PayuForm
          key={'pro_membership_plan_button'}
          title={'Buy Now @ ₹2000/6 months'}
          refCode={queryParams.refcode?.toString() || '30'}
          pageId={PAGELIST_ID['jd'] || '0'}
          tracking={{action: 'jsSCBanner_PayClick', category: 'UpgradeToPro'}}
          autoTrigger={isRetry}
          sx={{height: 32, marginTop: 1}}
          product={PaidProducts.PRO_MEMBERSHIP}
        />
        <Typography
          fontSize={theme.typography.pxToRem(8)}
          fontStyle={'italic'}
          fontWeight={500}
          lineHeight={'10px'}
          letterSpacing={'0.32px'}
          mt={0.75}
          mb={{xxs: 0, md: 1.75}}
          display={{xxs: 'none', md: 'block'}}
        >
          {`(Less than Rs 15/day)`}
        </Typography>
      </>
    );
  };

  const TickCheck = ({
    item,
    index,
    marginTop,
  }: {
    item: boolean;
    index: string;
    marginTop: {xxs?: number; sm?: number; md?: number};
  }) => (
    <Box mt={marginTop}>
      <Image
        key={index}
        src={item ? OutlineCicleCheck : OutlineCircleCross}
        alt={item ? 'Completed' : 'Uncompleted'}
        width={isLarge ? 28 : 16}
        height={isLarge ? 28 : 16}
        objectFit="cover"
      />
    </Box>
  );

  const INDEX_SPACING: {[key: string]: {[key: number]: number}} = {
    basic: {
      0: 2.25,
      1: 2,
      2: 4.25,
      3: 3.5,
      4: 2.25,
      5: 2.25,
    },
    pro: {
      0: 2.25,
      1: 2,
      2: 4.25,
      3: 3.5,
      4: 2.25,
      5: 2.25,
    },
  };

  const INDEX_SPACING_SM: {[key: string]: {[key: number]: number}} = {
    basic: {
      0: 2.5,
      1: 3,
      2: 3.5,
      3: 2.5,
      4: 2.5,
      5: 3,
    },
    pro: {
      0: 0.5,
      1: 3,
      2: 3.5,
      3: 2.5,
      4: 2.5,
      5: 3,
    },
  };

  return (
    <WrapperComponent
      open={open}
      maxWidth={'lg'}
      key={'pro_membership_popup'}
      data-testid={'pro_membership_popup'}
      title={title ? title : 'Power up your job search with '}
      subtitle={subtitle ? subtitle : ''}
      titleExtension="PRO-Membership"
      maxHeight={isLarge ? 600 : 579}
      onClose={handleClose}
      variant={VersionVariants.VERSION_THREE}
      bannerMode={false}
      defaultSnap={({lastSnap, snapPoints}: any) =>
        lastSnap ?? Math.max(...snapPoints)
      }
      snapPoints={({maxHeight}: {maxHeight: number}) => [maxHeight - 30]}
      onDismiss={handleClose}
    >
      <Grid
        container
        borderRadius={{xxs: 0, md: 4}}
        p={1}
        sx={{
          backgroundColor: 'border.main',
          justifyContent: 'center',
        }}
      >
        <Grid
          item
          xxs={6.2}
          md={5.7}
          sx={{
            mt: {xxs: 3.6, md: 5.25},
            width: {xxs: 154, md: 344},
          }}
        >
          <Typography
            fontSize={{
              xxs: theme.typography.pxToRem(12),
              md: theme.typography.pxToRem(14),
            }}
            fontWeight={600}
            lineHeight={{xxs: '16px', md: '24px'}}
            mb={1.5}
          >
            Membership Features
          </Typography>
          <List
            sx={{
              paddingLeft: theme.spacing(2),
            }}
          >
            {planPopUp?.infos.map((item, index) => {
              return (
                <ListItem
                  component="a"
                  key={`profiledrawer-listitem-${index}`}
                  title={item.title}
                  target="_self"
                  sx={{
                    display: 'list-item',
                    listStyleType: 'disc',
                    fontSize: theme.typography.pxToRem(12),
                    fontWeight: {
                      xxs: theme.typography.fontWeightMedium,
                      md: theme.typography.fontWeightBold,
                    },
                    whiteSpace: 'wrap',
                    color: theme.palette.secondary.main,
                    wordBreak: 'break-word',
                    ':hover': {},
                    p: `0px 0px ${isLarge ? '14px' : '24px'} 0px`,
                  }}
                >
                  {item.title}
                  <br />
                  {isLarge && (
                    <Typography
                      variant="body3"
                      color={theme.palette.text.secondary}
                    >
                      {item.subTitle}
                    </Typography>
                  )}
                </ListItem>
              );
            })}
          </List>
        </Grid>
        <Grid
          item
          display={'flex'}
          ml={{xxs: 2, md: 4}}
          mt={{xxs: 2, sm: 1}}
          textAlign={'center'}
        >
          <Box
            borderRadius={4}
            display={'inline-block'}
            boxShadow={`0px 6.48px 6px 2px rgba(41, 41, 41, 0.02) inset`}
            sx={{
              backgroundColor: 'white',
              width: {xxs: '48px', md: '120px'},
              height: {xxs: '323px', sm: '420px', md: '456px'},
            }}
          >
            <Typography
              color={'text.secondary'}
              fontSize={{xxs: 10, md: 16}}
              sx={{
                px: {xxs: 1.25, md: 4.75},
                pt: {xxs: 1.625, md: 5.25},
              }}
              fontWeight={theme.typography.fontWeightMedium}
            >
              Basic
            </Typography>
            {planPopUp?.basic.map((item, index) => (
              <TickCheck
                key={`basic_${index}`}
                item={item}
                index={`basic_${index}`}
                marginTop={{
                  xxs: INDEX_SPACING_SM['basic'][index],
                  md: INDEX_SPACING['basic'][index],
                }}
              />
            ))}
          </Box>
          <Box
            borderRadius={{xxs: 1.5, md: 4}}
            border={`1px solid ${theme.palette.border.light}`}
            display={'inline-block'}
            boxShadow={`0px 6.48px 6px 2px rgba(41, 41, 41, 0.02)`}
            sx={{
              backgroundColor: 'white',
              width: {xxs: '94px', md: '221px'},
              flexShrink: 0,
            }}
          >
            <Typography
              color={'white'}
              fontSize={{
                xxs: theme.typography.pxToRem(10),
                md: theme.typography.pxToRem(16),
              }}
              fontWeight={600}
              lineHeight={'24px'}
              sx={{
                borderRadius: {xxs: 1.5, md: 4},
                px: {xxs: 4.25, md: 11.5},
                py: {xxs: 1, md: 2.25},
                margin: 0.25,
                background: 'linear-gradient(90deg, #225448 0%, #7E9F67 100%)',
              }}
            >
              PRO
            </Typography>
            {planPopUp?.pro.map((item, index) => (
              <TickCheck
                key={`pro_${index}`}
                item={item}
                index={`pro_${index}`}
                marginTop={{
                  xxs: INDEX_SPACING_SM['pro'][index],
                  md: INDEX_SPACING['pro'][index],
                }}
              />
            ))}
            {isAboveMd && <ProButton />}
          </Box>
        </Grid>
        <Grid item>
          <Box display={{xxs: 'block', md: 'none'}} textAlign={'center'}>
            <ProButton />
          </Box>
        </Grid>
      </Grid>
    </WrapperComponent>
  );
};

export default ProMembershipPlanPopUp;
